"use client";

import { useDebug } from "@/mono/lib/hooks/useDebug";
import useDeferredFacade from "@/mono/lib/hooks/useDeferredFacade";
import { useScrollRigScrollTrigger } from "@/mono/lib/hooks/useScrollRigScrollTrigger";
import {
  ScrollScene,
  UseCanvas,
  useTracker,
} from "@/mono/third-party/r3f-scroll-rig-custom";
import { getProcessedSrc } from "@/lib/processedSrc";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { useGlobalStore } from "@/mono/store/global";
import NineGLImage from "./index";

const EASE_CUSTOM_4_CSS = "cubic-bezier(0.22, 1, 0.36, 1)";

const DOM_WRAPPER_STYLE = {
  overflow: "hidden",
  borderRadius: "10px",
  position: "relative",
};

const DOM_MEDIA_RADIUS = "10px";

const DOM_MEDIA_HOVER_STYLE = {
  transform: "scale(1.05)",
  filter: "brightness(0.75)",
};

const MAX_RETRIES = 2;

const DOMFallback = forwardRef(
  (
    {
      src: _src,
      alt,
      className = "",
      isVideo = false,
      animateOnScroll = true,
      offset = 0,
      onFacadeReady,
      onTextureReady,
      darken,
      video,
      ...props
    },
    ref
  ) => {
    const el = useRef();
    const mediaRef = useRef();
    const isHovered = useRef(false);
    const isVisible = useRef(false);
    const videoRetries = useRef(0);
    const videoActivated = useRef(false);
    const isMobileLayout = useGlobalStore((s) => s.isMobileLayout);
    const scaleDuration = isMobileLayout ? 1 : 1.4;
    const useHover = props.useHover !== false;
    const isLink = props.isLink !== false;
    const canHover = useHover && isLink;

    const videoSrc = useMemo(() => {
      if (!isVideo) return null;

      const isWebmUrl = (url) => url && url.includes(".webm");

      if (video?.mp4Url) {
        return getProcessedSrc(video.mp4Url);
      }

      if (_src && !_src.includes(".m3u8") && !isWebmUrl(_src)) {
        return getProcessedSrc(_src);
      }

      if (video?.streamingUrl) {
        return getProcessedSrc(video.streamingUrl);
      }

      // _src is .webm with no mp4/streaming fallback — use it as last resort
      // (will fail on Safari but there's nothing else available)
      if (_src) return getProcessedSrc(_src);

      return null;
    }, [isVideo, video, _src]);

    const posterSrc = useMemo(() => {
      if (!isVideo) return undefined;
      return video?.thumbnailUrl
        ? getProcessedSrc(video.thumbnailUrl)
        : undefined;
    }, [isVideo, video]);

    const imageSrc = useMemo(
      () => (!isVideo && _src ? getProcessedSrc(_src) : null),
      [isVideo, _src]
    );

    const baseFilter = darken ? `brightness(${1 - darken})` : "";

    const applyHover = useCallback(
      (hover) => {
        if (!canHover || !mediaRef.current) return;
        isHovered.current = hover;
        if (hover) {
          mediaRef.current.style.transform = DOM_MEDIA_HOVER_STYLE.transform;
          mediaRef.current.style.filter = DOM_MEDIA_HOVER_STYLE.filter;
        } else {
          mediaRef.current.style.transform = "scale(1)";
          mediaRef.current.style.filter = baseFilter;
        }
      },
      [canHover, baseFilter]
    );

    const activateVideo = useCallback(() => {
      const videoEl = mediaRef.current;
      if (!videoEl || videoEl.tagName !== "VIDEO" || videoActivated.current)
        return;
      if (!videoSrc) return;

      videoActivated.current = true;
      videoEl.load();
    }, [videoSrc]);

    const tryPlayVideo = useCallback((videoEl) => {
      if (!videoEl || !videoEl.play) return;
      if (!videoEl.paused) return;
      const p = videoEl.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          setTimeout(() => {
            if (videoEl.paused) {
              const retry = videoEl.play();
              if (retry?.catch) retry.catch(() => {});
            }
          }, 200);
        });
      }
    }, []);

    const facade = useMemo(
      () => ({
        animateIn: () => {
          if (mediaRef.current) {
            isVisible.current = true;
            if (isVideo) {
              activateVideo();
              tryPlayVideo(mediaRef.current);
            }
            requestAnimationFrame(() => {
              if (!mediaRef.current) return;
              mediaRef.current.style.opacity = "1";
              mediaRef.current.style.transform = "scale(1)";
              mediaRef.current.style.filter = baseFilter || "";
            });
          }
        },
        animateOut: () => {
          if (mediaRef.current) {
            mediaRef.current.style.opacity = "0";
            mediaRef.current.style.transform = `scale(0.5)`;
            isVisible.current = false;
          }
        },
        visible: () => isVisible.current,
        playVideo: () => {
          if (isVideo && mediaRef.current) {
            activateVideo();
            mediaRef.current.currentTime = 0;
            tryPlayVideo(mediaRef.current);
          }
        },
        stopVideo: () => {
          if (isVideo && mediaRef.current) mediaRef.current.pause?.();
        },
        seekVideo: (time) => {
          if (isVideo && mediaRef.current)
            mediaRef.current.currentTime = time || 0;
        },
        triggerPointerOver: () => applyHover(true),
        triggerPointerOut: () => applyHover(false),
        getElementSize: () => {
          if (!el.current) return { width: 0, height: 0 };
          const r = el.current.getBoundingClientRect();
          return { width: r.width, height: r.height };
        },
      }),
      [isVideo, applyHover, baseFilter, tryPlayVideo, activateVideo]
    );

    useImperativeHandle(ref, () => facade, [facade]);

    useScrollRigScrollTrigger(
      {
        el: animateOnScroll ? el : { current: null },
        offset,
        handleIn: () => {
          if (!animateOnScroll || isVisible.current) return;
          facade.animateIn();
        },
        handleOut: () => {},
      },
      [animateOnScroll, offset]
    );

    const facadeNotified = useRef(false);
    useEffect(() => {
      if (!facadeNotified.current) {
        facadeNotified.current = true;
        onFacadeReady?.(facade);
      }
    }, [facade, onFacadeReady]);


    const handleLoad = useCallback(() => {
      onTextureReady?.();
    }, [onTextureReady]);

    const handleCanPlay = useCallback(() => {
      if (isVisible.current) {
        tryPlayVideo(mediaRef.current);
      }
    }, [tryPlayVideo]);

    const handleVideoError = useCallback(() => {
      const videoEl = mediaRef.current;
      if (!videoEl || !videoSrc) return;
      if (videoRetries.current < MAX_RETRIES) {
        videoRetries.current++;
        setTimeout(() => videoEl.load(), 1000);
      }
    }, [videoSrc]);

    const handleImageError = useCallback(
      (e) => {
        const imgEl = e.target;
        if (!imgEl || !imageSrc) return;
        if (!imgEl._retryCount) imgEl._retryCount = 0;
        if (imgEl._retryCount < MAX_RETRIES) {
          imgEl._retryCount++;
          const sep = imageSrc.includes("?") ? "&" : "?";
          imgEl.src = `${imageSrc}${sep}_retry=${imgEl._retryCount}`;
        }
      },
      [imageSrc]
    );

    return (
      <div
        className={className}
        ref={el}
        style={DOM_WRAPPER_STYLE}
        onMouseEnter={canHover ? () => applyHover(true) : undefined}
        onMouseLeave={canHover ? () => applyHover(false) : undefined}
      >
        {isVideo && videoSrc ? (
          <video
            ref={mediaRef}
            src={videoSrc}
            poster={posterSrc}
            loop
            muted
            playsInline
            preload="none"
            controls={false}
            style={{
              width: "100%",
              height: "100%",
              display: "block",
              objectFit: "cover",
              opacity: 0,
              transform: "scale(0.5)",
              transition: `transform ${scaleDuration}s ${EASE_CUSTOM_4_CSS}, opacity ${scaleDuration}s ${EASE_CUSTOM_4_CSS}, filter 0.6s ease`,
              filter: baseFilter,
              borderRadius: DOM_MEDIA_RADIUS,
            }}
            onLoadedData={(e) => {
              handleLoad();
              handleCanPlay();
            }}
            onCanPlay={handleCanPlay}
            onError={handleVideoError}
          />
        ) : imageSrc ? (
          <img
            ref={mediaRef}
            src={imageSrc}
            alt={alt || ""}
            loading="eager"
            style={{
              width: "100%",
              display: "block",
              objectFit: "cover",
              opacity: 0,
              transform: "scale(0.5)",
              transition: `transform ${scaleDuration}s ${EASE_CUSTOM_4_CSS}, opacity ${scaleDuration}s ${EASE_CUSTOM_4_CSS}, filter 0.6s ease`,
              filter: baseFilter,
              borderRadius: DOM_MEDIA_RADIUS,
            }}
            onLoad={handleLoad}
            onError={handleImageError}
          />
        ) : null}
      </div>
    );
  }
);

const NineGLImageElement = forwardRef(
  (
    {
      src,
      alt,
      animateOnScroll = true,
      shouldHandleOut = false,
      className = "",
      isVideo = false,
      scrollSceneConfig = {},
      debug = false,
      offset = 0,
      tMap = null,
      onFacadeReady,
      onTextureReady,
      ...props
    },
    ref
  ) => {
    const noWebGLImages = useGlobalStore((s) => s.noWebGLImages);

    if (noWebGLImages) {
      return (
        <DOMFallback
          ref={ref}
          src={src}
          alt={alt}
          className={className}
          isVideo={isVideo}
          animateOnScroll={animateOnScroll}
          offset={offset}
          onFacadeReady={onFacadeReady}
          onTextureReady={onTextureReady}
          darken={props.darken}
          useHover={props.useHover}
          isLink={props.isLink}
          video={props.video}
        />
      );
    }

    return (
      <GLImageElement
        ref={ref}
        src={src}
        alt={alt}
        animateOnScroll={animateOnScroll}
        shouldHandleOut={shouldHandleOut}
        className={className}
        isVideo={isVideo}
        scrollSceneConfig={scrollSceneConfig}
        debug={debug}
        offset={offset}
        tMap={tMap}
        onFacadeReady={onFacadeReady}
        onTextureReady={onTextureReady}
        {...props}
      />
    );
  }
);

const GLImageElement = forwardRef(
  (
    {
      src,
      alt,
      animateOnScroll = true,
      shouldHandleOut = false,
      className = "",
      isVideo = false,
      scrollSceneConfig = {},
      debug = false,
      offset = 0,
      tMap = null,
      onFacadeReady,
      onTextureReady,
      ...props
    },
    ref
  ) => {
    const el = useRef();
    const img = useRef();
    const internalRef = useRef();
    const { facade, attach } = useDeferredFacade(
      internalRef,
      {},
      [],
      [
        "animateIn",
        "animateOut",
        "visible",
        "playVideo",
        "stopVideo",
        "seekVideo",
        "triggerPointerOver",
        "triggerPointerOut",
        "getElementSize",
      ]
    );
    const facadeNotified = useRef(false);
    const handleAttach = useCallback(
      (instance) => {
        attach(instance);
        if (instance && !facadeNotified.current) {
          facadeNotified.current = true;
          onFacadeReady?.(facade);
        }
      },
      [attach, onFacadeReady, facade]
    );

    const transforms = useTracker(el);

    useScrollRigScrollTrigger(
      {
        el: animateOnScroll ? el : { current: null },
        offset,
        handleIn: () => {
          const target = internalRef.current;
          if (!target) return;
          if (!animateOnScroll || target.visible?.()) return;
          target.animateIn();
        },
        handleOut: () => {},
      },
      [animateOnScroll, offset]
    );

    const renderHiddenVideoElement = useMemo(() => {
      return Boolean(isVideo && src);
    }, [isVideo, src]);

    useImperativeHandle(ref, () => facade, [facade]);

    const debugOverlap = useDebug() || props.hide;

    return (
      <>
        <div className={className} ref={el}>
          {!isVideo && src && (
            <img
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                visibility: debugOverlap ? "visible" : "hidden",
                opacity: debugOverlap ? 0.8 : 0,
              }}
              crossOrigin="anonymous"
              ref={img}
              src={src}
              alt={alt}
            />
          )}
          {renderHiddenVideoElement && (
            <video
              ref={img}
              src={getProcessedSrc(src)}
              crossOrigin="anonymous"
              loop
              muted
              playsInline
              webkit-playsinline=""
              preload="auto"
              autoPlay
              controls={false}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                visibility: "hidden",
              }}
            />
          )}
        </div>

        <UseCanvas>
          <ScrollScene
            track={el}
            hideOffscreen={animateOnScroll}
            overrideVisible={!animateOnScroll}
            {...scrollSceneConfig}
          >
            {(scrollSceneProps) => (
              <NineGLImage
                ref={handleAttach}
                tMap={tMap}
                imgRef={img}
                isVideo={isVideo}
                src={src}
                transforms={transforms}
                {...scrollSceneProps}
                {...props}
                debug={debug}
                onReady={onTextureReady}
              />
            )}
          </ScrollScene>
        </UseCanvas>
      </>
    );
  }
);

export default NineGLImageElement;
